import { addDays } from "date-fns";
import { motion } from "framer-motion";
import { ReactNode, useEffect, useState } from "react";
import { useToastMessage, useViewport } from "src/contexts";
import { DateOnly } from "src/utils/dates";
import {
  BasicOverlay,
  Button,
  Icon,
  NewFieldLabel,
  NewFieldStyleOverrides,
  PhoneField,
  Radio,
  TextField,
  TransparentHBLoadingSpinnerOnly,
} from "~components";
import { Css } from "~generated/css";
import { CheckoutConfigStatus } from "~generated/graphqlTypes";
import { cleanValidationError, isValidPhone, removePilotPrefix, useTestIds } from "~utils";
import {
  ReserveYourBuildFlyout_CheckoutConfigFragment,
  useReserveYourBuild_SaveCheckoutConfigMutation,
} from "./ReserveYourBuildFlyout.generated";

type ReserveBuildFormState = {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  configurationName: string | null;
};

type ReserveYourBuildFlyoutProps = {
  onClose: () => void;
  checkoutConfig: ReserveYourBuildFlyout_CheckoutConfigFragment;
  expirationInDays: number;
  isSummaryStep?: boolean;
  currentUserEmail?: string;
  currentUserName?: string;
  isHomeowner?: boolean;
};

export function ReserveYourBuildFlyout(props: ReserveYourBuildFlyoutProps) {
  const { onClose, checkoutConfig, expirationInDays, isSummaryStep, currentUserEmail, currentUserName, isHomeowner } =
    props;
  const tid = useTestIds(props, "reserve-your-build-flyout");
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [userAcknowledgement, setUserAcknowledgement] = useState(false);
  const { isMobile } = useViewport();

  const [saveConfig] = useReserveYourBuild_SaveCheckoutConfigMutation();
  const { showToast } = useToastMessage();

  // Split the current user's name into first and last name
  const [defaultFirstName = "", ...lastNameParts] = currentUserName?.trim().split(/\s+/) || [];
  const defaultLastName = lastNameParts.join(" ");

  const [form, setForm] = useState<ReserveBuildFormState>(() => {
    return {
      // pre-fill form with existing data if it exists, fallback to current user data
      firstName: checkoutConfig.customerFirstName || defaultFirstName,
      lastName: checkoutConfig.customerLastName || defaultLastName,
      email: checkoutConfig.customerEmail || currentUserEmail || "",
      phoneNumber: checkoutConfig.customerPhone || "",
      configurationName: removePilotPrefix(checkoutConfig.name),
    };
  });

  const shouldShowReserve = isSummaryStep && !isMobile;

  async function onSubmit() {
    setError(false);
    setSaving(true);

    await saveConfig({
      variables: {
        input: {
          id: checkoutConfig.id,
          expiresAt: new DateOnly(addDays(new Date(), expirationInDays)),
          // Only reserve if on summary step and not on mobile
          ...(shouldShowReserve && { status: CheckoutConfigStatus.Reserved }),
          name: form.configurationName,
          customerEmail: form.email,
          customerFirstName: form.firstName,
          customerLastName: form.lastName,
          customerPhone: form.phoneNumber,
        },
      },
      onError: (error) => {
        const cleanErrorMessage = cleanValidationError(error.message);
        showToast({
          variant: "error",
          title: "Save Error",
          body: `We're having trouble saving this build: ${cleanErrorMessage}`,
        });
      },
    });
    onClose();
  }

  return (
    <Flyout testId="reserve-build-flyout" onClose={onClose} {...tid}>
      <form>
        <div css={Css.pt4.t32.ifMobileOrTablet.fsPx(40).$}>
          {shouldShowReserve ? "Reserve Your Home" : "Save Your Design"}
        </div>
        <div css={Css.mt2.t14.gray.$}>
          {shouldShowReserve
            ? `Get a detailed quote of your home. Your pricing will be locked in for ${expirationInDays} days.`
            : "Provide your email to save your progress. Pricing is subject to change based on market supply chain price changes. Pricing on your build is only locked after reserving. "}
        </div>
        {/* FIELDS */}
        {/* Configuration Name*/}
        <div css={Css.relative.$}>
          {saving && (
            <motion.div
              initial={{ backdropFilter: "blur(0px)" }}
              animate={{ backdropFilter: "blur(.5px)" }}
              exit={{ backdropFilter: "blur(0px)" }}
              css={Css.absolute.w100.h100.df.aic.jcc.z1.$}
            >
              <TransparentHBLoadingSpinnerOnly />
            </motion.div>
          )}
          <div css={Css.mt3.$}>
            <TextField
              disabled={saving}
              required
              value={form.configurationName || undefined}
              onChange={(e, buildName) => setForm((prev) => ({ ...prev, configurationName: buildName }))}
              placeholder="Your Dream Home"
              label={NewFieldLabel("Build Name*")}
              cssOverrides={NewFieldStyleOverrides}
              {...tid.buildName}
            />
          </div>
          {/* Names */}
          <div css={Css.df.gap3.$}>
            <div css={Css.mt3.w100.$}>
              <TextField
                disabled={saving}
                required
                value={form.firstName || undefined}
                onChange={(e, firstName) => setForm((prev) => ({ ...prev, firstName }))}
                placeholder="First Name"
                label={NewFieldLabel("First Name*")}
                cssOverrides={NewFieldStyleOverrides}
                {...tid.firstName}
              />
            </div>
            <div css={Css.mt3.w100.$}>
              <TextField
                disabled={saving}
                required
                value={form.lastName || undefined}
                onChange={(e, lastName) => setForm((prev) => ({ ...prev, lastName }))}
                placeholder="Last Name"
                label={NewFieldLabel("Last Name*")}
                cssOverrides={NewFieldStyleOverrides}
                {...tid.lastName}
              />
            </div>
          </div>
          {/* Email */}
          <div css={Css.mt3.$}>
            <TextField
              disabled={saving || isHomeowner}
              value={form.email || undefined}
              onChange={(e, email) => setForm((prev) => ({ ...prev, email }))}
              placeholder="Email"
              label={NewFieldLabel("Email*")}
              cssOverrides={NewFieldStyleOverrides}
              {...tid.emailAddress}
            />
          </div>
          {/* Phone Number */}
          <div css={Css.mt3.$}>
            <PhoneField
              disabled={saving}
              useConfigurationStyle
              value={form.phoneNumber || undefined}
              onChange={(phoneNumber) => setForm((prev) => ({ ...prev, phoneNumber }))}
              placeholder="(XXX) XXX-XXXX"
              {...tid.phoneNumber}
            />
          </div>
          {error ? (
            <div css={Css.mt5.t14.redError.$}>Oops! Something went wrong, please resubmit form.</div>
          ) : (
            <div css={Css.mt6.$} />
          )}
        </div>
        <div css={Css.df.fdc.gap3.$}>
          {shouldShowReserve && (
            <div css={Css.df.gap2.$}>
              <div>
                <Radio
                  selected={userAcknowledgement}
                  onClick={() => setUserAcknowledgement((prev) => !prev)}
                  {...tid.userAcknowledgement_button}
                />
              </div>
              <div css={Css.t14.gray800.$}>
                I understand that by reserving my build, I will not be able to make any changes.
              </div>
            </div>
          )}
          <Button
            disabled={
              !(
                form.configurationName &&
                form.configurationName.length > 0 &&
                form.lastName &&
                form.firstName &&
                form.firstName.length > 0 &&
                form.lastName.length > 0 &&
                form.email.match(/.+@.+\..+/) &&
                isValidPhone(form.phoneNumber) &&
                // Only require user acknowledgment on reservation
                (shouldShowReserve ? userAcknowledgement : true)
              ) || saving
            }
            onClick={() => onSubmit()}
            type="button"
            variant="primary"
            xss={Css.borderRadius("17.5px").$}
            {...tid.submit_button}
          >
            {shouldShowReserve ? "Reserve" : "Save"}
          </Button>
        </div>
      </form>
      {isSummaryStep && (
        <div css={Css.df.fdc.gap2.mt3.t10.gray.$}>
          <div>
            By clicking {shouldShowReserve ? "Reserve" : "Save"}, your home design will be saved. A Homebound
            representative will connect with you to confirm availability and pricing for your home.
          </div>
          <div>
            Reserving Now also confirms your place in the Homebound queue for homes to be built; this means you will
            have preference getting your build started based on the time of your reservation.
          </div>
          <div>* This is an estimated price and options will need to be confirmed by a sales rep.</div>
        </div>
      )}
    </Flyout>
  );
}

type FlyoutProps = {
  flyoutMaxWidth?: number;
  onClose: () => void;
  children: ReactNode;
  testId: string;
};

function Flyout(props: FlyoutProps) {
  const { flyoutMaxWidth = 448, onClose, children } = props;
  const tid = useTestIds(props, "flyout");

  useEffect(() => {
    // When the flyout is shown, we want to prevent scrolling
    document.body.style.overflow = "hidden";
    return () => {
      // When the flyout is hidden, we want to allow scrolling again
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <BasicOverlay onClick={onClose}>
      <motion.div
        css={
          Css.h("100vh")
            .wPx(flyoutMaxWidth)
            .oa.bgWhite.boxShadow("0px 4px 8px rgba(53, 53, 53, 0.1), 0px 2px 24px rgba(53, 53, 53, 0.08)")
            .fixed.container().oxh.ifMobileOrTablet.w100.$
        }
        initial={{ right: -flyoutMaxWidth }}
        animate={{ right: 0 }}
        transition={{ duration: 1 }}
        {...tid}
      >
        <div css={Css.pt5.px3.ifMobileOrTablet.pt8.px3.$} onClick={(e) => e.stopPropagation()}>
          <button css={Css.absolute.topPx(30).rightPx(30).$} onClick={onClose} {...tid.closeButton}>
            <Icon name="x" />
          </button>
          {children}
        </div>
      </motion.div>
    </BasicOverlay>
  );
}
